/**
 * Aba "Pedidos & orçamentos" do Cliente 360. Espelha o mockup:
 * 4 stats + seletor (Pedidos / Orçamentos / Ambientes) + 3 tabelas.
 * Pedidos e ambientes vêm do cockpit; orçamentos via GET /api/orcamentos
 * ?cliente={id}. Material/Dimensão do ambiente ficam "—" (só existem em
 * projeto CAD/DinaBox, não no snapshot manual) — honesto. Abaixo, o cadastro
 * de ambientes manuais do cliente (gestores).
 */
import React, { useEffect, useState } from "react";
import { FiChevronDown, FiChevronRight, FiSearch } from "react-icons/fi";

import { listarPedidosImportados } from "../../../../services/comercial";
import AmbientesClienteSection from "../AmbientesClienteSection";
import { Badge, fmtBRLCheio, fmtBRLCompacto, fmtDataCurta, Stat } from "../cli360";

function listaDe(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function pedidoTone(s) {
  const v = (s || "").toUpperCase();
  if (v.includes("CANCEL")) return "danger";
  if (v.includes("ENTREGUE") || v.includes("CONCLU")) return "success";
  if (v.includes("EXPED") || v.includes("ENTREG")) return "info";
  if (v.includes("PRODUC") || v.includes("PRODUÇ")) return "warning";
  return "neutral";
}

function orcamentoTone(s) {
  const v = (s || "").toUpperCase();
  if (v.includes("APROV")) return "success";
  if (v.includes("CONVERT")) return "accent";
  if (v.includes("RECUS") || v.includes("EXPIR")) return "danger";
  if (v.includes("NEGOCI") || v.includes("ENVIAD")) return "warning";
  return "neutral";
}

function PedidosTab({ cockpit, pedidos = [], clienteId, request, navigate, podeGerirAmbientes, onAbrirAmbiente }) {
  const ambientesResumo = cockpit?.ambientes_resumo || [];
  const [sub, setSub] = useState("pedidos");
  const [busca, setBusca] = useState("");
  const [orcamentos, setOrcamentos] = useState(null); // null = carregando
  const [importados, setImportados] = useState([]);
  const [expandido, setExpandido] = useState(() => new Set());

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await request(`/api/orcamentos?cliente=${clienteId}`, { silentError: true });
        const arr = Array.isArray(data) ? data : data?.results || [];
        if (vivo) setOrcamentos(arr);
      } catch {
        if (vivo) setOrcamentos([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [request, clienteId]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await listarPedidosImportados(request, clienteId, { silentError: true });
        if (vivo) setImportados(listaDe(data));
      } catch {
        if (vivo) setImportados([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [request, clienteId]);

  const toggleImportado = (id) =>
    setExpandido((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  const totalPedidos = pedidos.reduce((s, p) => s + (Number(p.valor_total) || 0), 0);
  const entregues = pedidos.filter((p) => p.entregue).length;
  const andamento = pedidos.filter((p) => !p.entregue);
  const valorAndamento = andamento.reduce((s, p) => s + (Number(p.valor_total) || 0), 0);
  const margens = pedidos.map((p) => p.margem).filter((m) => m != null);
  const margemMedia = margens.length ? Math.round(margens.reduce((a, b) => a + b, 0) / margens.length) : null;

  const termo = busca.trim().toLowerCase();
  const fPedidos = pedidos.filter(
    (p) => !termo || `${p.numero} ${(p.ambientes || []).map((a) => a.nome).join(" ")}`.toLowerCase().includes(termo)
  );
  const fOrc = (orcamentos || []).filter((o) => !termo || (o.numero || "").toLowerCase().includes(termo));
  const fAmb = ambientesResumo.filter(
    (a) => !termo || `${a.nome} ${a.pedido_numero}`.toLowerCase().includes(termo)
  );
  const fImportados = importados.filter(
    (p) =>
      !termo ||
      `${p.numero} ${(p.ambientes || []).map((a) => a.nome).join(" ")}`
        .toLowerCase()
        .includes(termo)
  );

  return (
    <section className="tab-panel">
      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat label="Total em pedidos" value={fmtBRLCheio(totalPedidos)} sub={`${pedidos.length} pedidos`} />
        <Stat
          label="Em andamento"
          value={
            <>
              {andamento.length}{" "}
              <span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>
                / {fmtBRLCompacto(valorAndamento)}
              </span>
            </>
          }
          sub="produção + entrega"
        />
        <Stat label="Entregues" value={entregues} sub={`de ${pedidos.length}`} />
        <Stat
          label="Margem média"
          value={margemMedia != null ? `${margemMedia}%` : "—"}
          sub="dos pedidos"
          tone={margemMedia != null && margemMedia >= 30 ? "pos" : undefined}
        />
      </div>

      <div className="flex between center wrap" style={{ gap: 12, marginBottom: 14 }}>
        <div className="seg">
          <button type="button" className={sub === "pedidos" ? "on" : ""} onClick={() => setSub("pedidos")}>
            Pedidos · {pedidos.length}
          </button>
          <button type="button" className={sub === "orcamentos" ? "on" : ""} onClick={() => setSub("orcamentos")}>
            Orçamentos · {orcamentos ? orcamentos.length : "…"}
          </button>
          <button type="button" className={sub === "ambientes" ? "on" : ""} onClick={() => setSub("ambientes")}>
            Ambientes · {ambientesResumo.length}
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 32,
            padding: "0 11px",
            background: "var(--surface)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
            minWidth: 200,
          }}
        >
          <FiSearch size={14} aria-hidden="true" style={{ color: "var(--ink-3)" }} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar…"
            style={{ border: 0, background: "transparent", color: "var(--ink)", outline: 0, font: "inherit", fontSize: 12, width: "100%" }}
          />
        </div>
      </div>

      {sub === "pedidos" && (
        <>
        <div className="tbl-wrap">
          <table className="dtable">
            <thead>
              <tr>
                <th>Pedido</th>
                <th>Ambiente(s)</th>
                <th>Criado</th>
                <th>Status</th>
                <th>Entrega</th>
                <th className="num">Valor</th>
                <th className="num">Margem</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {fPedidos.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted sm">Nenhum pedido.</td>
                </tr>
              ) : (
                fPedidos.map((p) => (
                  <tr key={p.id}>
                    <td className="cmono">{p.numero}</td>
                    <td>{(p.ambientes || []).map((a) => a.nome).join(", ") || "—"}</td>
                    <td className="cmono">{fmtDataCurta(p.criado) || "—"}</td>
                    <td>
                      <Badge tone={pedidoTone(p.situacao)} dot>{p.situacao_display}</Badge>
                    </td>
                    <td className="cmono">{fmtDataCurta(p.entrega) || "—"}</td>
                    <td className="num">{fmtBRLCheio(p.valor_total)}</td>
                    <td className={`num ${p.margem != null && p.margem >= 30 ? "pos" : ""}`}>
                      {p.margem != null ? `${Math.round(p.margem)}%` : "—"}
                    </td>
                    <td>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate(`/pedidos/${p.id}`)}>
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {fImportados.length > 0 && (
          <PedidosImportadosBloco
            pedidos={fImportados}
            expandido={expandido}
            onToggle={toggleImportado}
          />
        )}
        </>
      )}

      {sub === "orcamentos" && (
        <div className="tbl-wrap">
          <table className="dtable">
            <thead>
              <tr>
                <th>Código</th>
                <th>Criado</th>
                <th>Status</th>
                <th>Validade</th>
                <th className="num">Valor</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orcamentos === null ? (
                <tr><td colSpan={6} className="muted sm">Carregando…</td></tr>
              ) : fOrc.length === 0 ? (
                <tr><td colSpan={6} className="muted sm">Nenhum orçamento.</td></tr>
              ) : (
                fOrc.map((o) => (
                  <tr key={o.id}>
                    <td className="cmono">{o.numero}</td>
                    <td className="cmono">{fmtDataCurta(o.created_at) || "—"}</td>
                    <td>
                      <Badge tone={orcamentoTone(o.situacao)} dot>{o.situacao_display || o.situacao}</Badge>
                    </td>
                    <td className="cmono">{fmtDataCurta(o.validade_em) || "—"}</td>
                    <td className="num">{fmtBRLCheio(o.valor_total)}</td>
                    <td>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/orcamentos/${o.id}`)}>
                        Abrir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {sub === "ambientes" && (
        <>
          <div className="tbl-wrap">
            <table className="dtable">
              <thead>
                <tr>
                  <th>Ambiente</th>
                  <th>Pedido</th>
                  <th className="num">Módulos</th>
                  <th>Material</th>
                  <th>Dimensão</th>
                  <th className="num">Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fAmb.length === 0 ? (
                  <tr><td colSpan={7} className="muted sm">Nenhum ambiente.</td></tr>
                ) : (
                  fAmb.map((a) => (
                    <tr key={a.id} style={{ cursor: "pointer" }} onClick={() => onAbrirAmbiente(a.id)} title="Ver produção e componentes">
                      <td>{a.nome}</td>
                      <td className="cmono">{a.pedido_numero}</td>
                      <td className="num">{a.modulos_count}</td>
                      <td className="muted">—</td>
                      <td className="muted">—</td>
                      <td className="num">{fmtBRLCheio(a.valor)}</td>
                      <td>
                        <Badge tone={pedidoTone(a.situacao)} dot>{a.situacao_display}</Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 16 }}>
            <AmbientesClienteSection request={request} clienteId={clienteId} podeGerir={podeGerirAmbientes} />
          </div>
        </>
      )}
    </section>
  );
}

const DATAS_PEDIDO_IMPORTADO = [
  ["liberacao_obra", "Liberação obra"],
  ["definicao_acabamentos", "Def. acabamentos"],
  ["medicao", "Medição"],
  ["executivo", "Executivo"],
];

/**
 * Pedidos historicos importados por planilha (comercial.PedidoImportado).
 * NAO sao Pedido real — badge "importado", clicaveis para expandir os
 * ambientes ("PEDIDO - AMBIENTE") + vendedor/consultor/datas.
 */
function PedidosImportadosBloco({ pedidos, expandido, onToggle }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div className="flex center" style={{ gap: 8, margin: "0 0 8px" }}>
        <span
          className="muted"
          style={{ fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}
        >
          Pedidos importados
        </span>
        <Badge tone="neutral">{pedidos.length}</Badge>
        <span className="muted" style={{ fontSize: 11 }}>
          histórico trazido por planilha — não entra em produção/financeiro
        </span>
      </div>
      <div className="tbl-wrap">
        <table className="dtable">
          <thead>
            <tr>
              <th style={{ width: 28 }} aria-hidden="true" />
              <th>Pedido</th>
              <th>Ambientes</th>
              <th>Venda</th>
              <th>Entrega prev.</th>
              <th className="num">Valor</th>
            </tr>
          </thead>
          <tbody>
            {pedidos.map((p) => {
              const aberto = expandido.has(p.id);
              const ambientes = p.ambientes || [];
              return (
                <React.Fragment key={p.id}>
                  <tr
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggle(p.id)}
                    title="Ver ambientes e detalhes"
                  >
                    <td style={{ color: "var(--ink-3)" }}>
                      {aberto ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    </td>
                    <td className="cmono">
                      {p.numero}{" "}
                      <Badge tone="neutral">importado</Badge>
                    </td>
                    <td>{ambientes.length ? ambientes.map((a) => a.nome).join(", ") : "—"}</td>
                    <td className="cmono">{fmtDataCurta(p.data_venda) || "—"}</td>
                    <td className="cmono">{fmtDataCurta(p.entrega_prevista) || "—"}</td>
                    <td className="num">{fmtBRLCheio(p.valor_total)}</td>
                  </tr>
                  {aberto && (
                    <tr>
                      <td aria-hidden="true" />
                      <td colSpan={5} style={{ paddingTop: 4, paddingBottom: 14 }}>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px 20px",
                            marginBottom: 10,
                            fontSize: 12,
                          }}
                        >
                          <span>
                            <span className="muted">Vendedor:</span> {p.vendedor_nome || "—"}
                          </span>
                          <span>
                            <span className="muted">Consultor:</span> {p.consultor_nome || "—"}
                          </span>
                          {DATAS_PEDIDO_IMPORTADO.map(([campo, label]) =>
                            p[campo] ? (
                              <span key={campo}>
                                <span className="muted">{label}:</span> {fmtDataCurta(p[campo])}
                              </span>
                            ) : null
                          )}
                        </div>
                        {ambientes.length === 0 ? (
                          <div className="muted sm">Sem ambientes.</div>
                        ) : (
                          <table className="dtable" style={{ width: "auto", minWidth: 320 }}>
                            <tbody>
                              {ambientes.map((a) => (
                                <tr key={a.id}>
                                  <td>
                                    {p.numero} - {a.nome}
                                  </td>
                                  <td className="num">{fmtBRLCheio(a.valor)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PedidosTab;
